import React, { useEffect, useRef, useCallback, useState } from 'react';
import { X, AlertTriangle, Database, Download, CheckCircle } from 'lucide-react';

/**
 * Props interface for the StorageWarningModal component
 */
export interface StorageWarningModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback function when the modal should close */
  onClose: () => void;
  /** Callback when user acknowledges and wants to dismiss permanently */
  onDismissPermanently: () => void;
}

const STORAGE_KEY = 'avent-horizon-storage-warning-dismissed';

/**
 * Hook to manage the first-time storage warning
 */
export function useStorageWarning(): {
  showWarning: boolean;
  openWarning: () => void;
  closeWarning: () => void;
  dismissPermanently: () => void;
} {
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    // Check if user has dismissed the warning before
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      // Small delay to let the app render first
      const timer = setTimeout(() => setShowWarning(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const openWarning = useCallback(() => setShowWarning(true), []);
  const closeWarning = useCallback(() => setShowWarning(false), []);
  const dismissPermanently = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShowWarning(false);
  }, []);

  return { showWarning, openWarning, closeWarning, dismissPermanently };
}

/**
 * StorageWarningModal - First-time warning about IndexedDB data persistence
 *
 * Features:
 * - Shows automatically on first visit
 * - Explains IndexedDB risks
 * - Encourages regular backups
 * - Can be dismissed permanently
 */
export const StorageWarningModal: React.FC<StorageWarningModalProps> = ({
  isOpen,
  onClose,
  onDismissPermanently,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Handle Escape key press
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose, onDismissPermanently, dontShowAgain]
  );

  // Handle click outside modal
  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === modalRef.current) {
        handleClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose, onDismissPermanently, dontShowAgain]
  );

  const handleClose = () => {
    if (dontShowAgain) {
      onDismissPermanently();
    } else {
      onClose();
    }
  };

  // Add/remove event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Focus trap
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="storage-warning-title"
      style={{
        animation: 'fade-in 0.3s ease-out',
      }}
    >
      {/* Backdrop with blur */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        style={{
          animation: 'fade-in 0.3s ease-out',
        }}
      />

      {/* Modal Content */}
      <div
        ref={contentRef}
        tabIndex={-1}
        className="relative max-w-lg w-full bg-gradient-to-br from-amber-900/90 via-gray-900/95 to-black border border-amber-500/50 rounded-xl shadow-2xl"
        style={{
          animation: 'scale-in 0.3s ease-out',
          boxShadow:
            '0 0 40px rgba(245, 158, 11, 0.3), 0 0 80px rgba(245, 158, 11, 0.1)',
        }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-900/90 to-gray-900/90 backdrop-blur-sm border-b border-amber-500/30 p-6 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h2
                id="storage-warning-title"
                className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-300"
              >
                Important: Local Storage Only
              </h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors duration-200 group"
            aria-label="Close modal"
          >
            <X
              size={24}
              className="text-gray-400 group-hover:text-white transition-colors"
            />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3 p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg">
            <Database className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-300 text-sm">
                Your vault data is stored <strong className="text-amber-300">only in this browser</strong> using IndexedDB.
                This data can be lost if you:
              </p>
              <ul className="mt-2 text-sm text-gray-400 space-y-1">
                <li>Clear your browser data or cookies</li>
                <li>Use private/incognito mode</li>
                <li>Uninstall the browser</li>
                <li>Reset your device</li>
              </ul>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
            <Download className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-300 text-sm">
                <strong className="text-green-300">We strongly recommend</strong> exporting your vault regularly
                using the export feature in the Vault tab. This creates a backup file you can import later.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
            <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-300 text-sm">
                <strong className="text-purple-300">Your data never leaves your device</strong> -
                this is a privacy feature, not a limitation. All cryptographic operations happen
                locally in your browser.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-amber-500/20 bg-black/30 rounded-b-xl space-y-4">
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-amber-500 focus:ring-amber-500 focus:ring-offset-0"
            />
            <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
              Don't show this message again
            </span>
          </label>

          <button
            onClick={handleClose}
            className="w-full py-3 px-4 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-[1.02]"
          >
            I Understand
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default StorageWarningModal;
